'use client';

import { Avatar as MuiAvatar } from '@mui/material';
import { memo } from 'react';

import { Tooltip } from './Tooltip';

type AvatarSize =
  | 'small'
  | 'medium'
  | 'large'
  | 'xLarge'
  | '2xLarge'
  | '3xLarge';

type AvatarProps = {
  name?: string;
  src?: string | null;
  size?: AvatarSize;
  alt?: string;
  sx?: object;
  showTooltip?: boolean;
};

function getInitials(name?: string): string {
  if (!name) return '?';

  const trimmed = name.trim();
  const parts = trimmed.split(' ');

  if (parts.length === 1) return parts[0][0].toUpperCase();

  const first = parts[0][0];
  const last = parts[parts.length - 1][0];

  return `${first}${last}`.toUpperCase();
}

const sizeMap: Record<AvatarSize, number> = {
  small: 24,
  medium: 34,
  large: 40,
  xLarge: 64,
  '2xLarge': 96,
  '3xLarge': 128,
} as const;

export const Avatar = memo(function Avatar({
  name,
  src,
  size = 'medium',
  alt = 'Avatar',
  sx = {},
  showTooltip = true,
}: AvatarProps) {
  const showInitials = !src;

  const avatar = (
    <MuiAvatar
      src={src ?? undefined}
      alt={alt}
      sx={{
        width: sizeMap[size],
        height: sizeMap[size],
        fontSize: (sizeMap[size] as number) * 0.4,
        bgcolor: showInitials ? 'var(--nn-accent-gray)' : 'transparent',
        ...sx,
      }}
    >
      {showInitials ? getInitials(name) : null}
    </MuiAvatar>
  );

  if (!showTooltip) {
    return avatar;
  }

  return <Tooltip title={name ?? ''}>{avatar}</Tooltip>;
});

Avatar.displayName = 'Avatar';
